/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './PepsMonitoringFeed.module.scss';

import type { PepsMonitoringFeedProps } from './PepsMonitoringFeed.types';

const COUNT_DURATION = 1400;

export const PepsMonitoringFeed = ({
  title = 'Monitoring alerts',
  watching,
  watchingLabel = 'entities · directors & UBOs included',
  watchingPrefix = 'Watching',
  liveLabel = 'LIVE',
  alerts,
  className,
}: PepsMonitoringFeedProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref, { threshold: 0.25 });

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    let raf = 0;
    let start: number | null = null;

    const step = (ts: number) => {
      if (start === null) {
        start = ts;
      }

      const progress = Math.min((ts - start) / COUNT_DURATION, 1);
      setCount(Math.floor(progress * watching));

      if (progress < 1) {
        raf = requestAnimationFrame(step);
      }
    };

    raf = requestAnimationFrame(step);

    return () => cancelAnimationFrame(raf);
  }, [visible, watching]);

  return (
    <div ref={ref} className={cn(styles.feed, visible && styles.visible, className)} aria-label="Monitoring alert feed">
      <div className={styles.top}>
        <div className={styles.topLeft}>
          <span className={styles.bell} aria-hidden>
            <svg viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1.8c-2.5 0-4 1.9-4 4.2 0 3.1-1.2 4-1.2 4h10.4S12 9.1 12 6c0-2.3-1.5-4.2-4-4.2Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path d="M6.6 12.6a1.5 1.5 0 0 0 2.8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>

          <div>
            <b>{title}</b>
            <small>
              {watchingPrefix} <span className={styles.count}>{count}</span> {watchingLabel}
            </small>
          </div>
        </div>

        <span className={styles.live}>
          <i />
          {liveLabel}
        </span>
      </div>

      {alerts.map((alert, i) => {
        const isOpen = open === alert.id;

        return (
          <div
            key={alert.id}
            className={cn(styles.alert, styles[`alert_${alert.tone}`], isOpen && styles.alertOpen)}
            style={{ transitionDelay: `${0.12 + i * 0.12}s` }}
            role="button"
            tabIndex={0}
            onClick={() => setOpen(isOpen ? null : alert.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(isOpen ? null : alert.id);
              }
            }}
          >
            <span className={styles.ai}>{alert.glyph}</span>

            <div className={styles.alertBody}>
              <em className={styles.acat}>{alert.category}</em>
              <b className={styles.atitle}>{alert.title}</b>
              <span className={styles.adesc}>{alert.description}</span>

              <div className={styles.drawer}>
                <div className={styles.drawerIn}>
                  {alert.drawer.map((row) => (
                    <div key={row.label} className={styles.drawerRow}>
                      <span>{row.label}</span>
                      <b className={row.valueTone ? styles[`val_${row.valueTone}`] : undefined}>{row.value}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <span className={styles.when}>{alert.time}</span>
          </div>
        );
      })}
    </div>
  );
};
